import React, { useMemo } from "react";
import VStack from "../../ui_atoms/VStack";
import { Linking, StyleSheet } from "react-native";
import { Cct, CctDetailsFieldFullValue, Contract, Mission } from "../../__generated__/graphql";
import { useI18n } from "../../hooks/i18n-hooks";
import { useRecoilValue } from "recoil";
import { allCCTState, defaultCCTState, liftBasedNoCCTState } from "../../atoms/core";
import Callout from "../../ui_atoms/Callout";
import { Buildable } from "ts-essentials";
import { useContractCct, useLoadCCTs } from "../../hooks/cct-utils";
import SelectField from "../SelectField";
import MissionCctDetails from "./MissionCctDetails";
import { BodyMediumText, BodySmallText, LinkSmallText } from "../FormattedText";
import Gap from "../../ui_atoms/Gap";
import { useIntercom } from "../../hooks/intercom";
import MarkdownView from "../MarkdownView";
import { useTheme } from "../../hooks/use-theme";
import { useCurrentProfile } from "../../hooks/use-current-profile";

type MissionCCTProps = {
    mission: Buildable<Mission>;
    setCCTId: (cctId?: Cct) => void;
    setCctDetails: (value: CctDetailsFieldFullValue) => void;
    setScreenIsValid: (value: boolean) => void;
};

const MissionCCT: React.FC<MissionCCTProps> = ({
    mission,
    setCCTId,
    setCctDetails,
    setScreenIsValid,
}) => {
    const cctsLoading = useLoadCCTs(mission.currentContract?.workingAddress?.locality?.state?.code);
    const { colors, gridSize } = useTheme();
    const currentProfile = useCurrentProfile();
    const isPrivateClient = currentProfile?.isPrivateClient || false;
    const i18n = useI18n();
    const { presentMessageComposer, presentArticleId } = useIntercom();
    const allCCT = useRecoilValue(allCCTState);
    // default CCT is the CCT Location de Service.
    const defaultCCT = useRecoilValue(defaultCCTState);
    const liftBasedNoCct = useRecoilValue(liftBasedNoCCTState);
    const cct_choices = useMemo(() => {
        if (allCCT && !cctsLoading) {
            const filteredCCT = allCCT
                .filter((cct) => {
                    return (
                        cct.cctEditionId !== defaultCCT?.cctEditionId &&
                        cct.cctEditionId !== liftBasedNoCct?.cctEditionId &&
                        cct.active
                    );
                })
                .map((cct) => {
                    return {
                        value: cct.cctContractId,
                        label: cct.name,
                    };
                });
            // We allow to choose the default CCT that is in fact the Location de Service only if we do not
            // have a CCt that correspond to No CCT.
            if (defaultCCT && defaultCCT.active && mission.legalEmployer?.useLseCct) {
                filteredCCT.unshift({
                    value: defaultCCT.cctContractId,
                    label: i18n.t("screens.user.create_mission_cct.no_cct"),
                });
            } else if (
                liftBasedNoCct &&
                liftBasedNoCct.active &&
                !mission.legalEmployer?.useLseCct
            ) {
                filteredCCT.unshift({
                    value: liftBasedNoCct.cctContractId,
                    label: i18n.t("screens.user.create_mission_cct.no_cct"),
                });
            }

            return filteredCCT;
        }
        return null;
    }, [liftBasedNoCct, allCCT, defaultCCT, cctsLoading, mission.legalEmployer?.useLseCct, i18n]);

    const missionCct = useContractCct(mission.currentContract as Contract);

    const cctLink = useMemo(() => {
        if (missionCct && missionCct.active) {
            if (liftBasedNoCct && missionCct.cctContractId === liftBasedNoCct.cctContractId) {
                return (
                    <MarkdownView
                        markdown={i18n.t("screens.user.create_mission_cct.no-cct-info")}
                    />
                );
            }
            if (!liftBasedNoCct || missionCct.cctContractId !== liftBasedNoCct.cctContractId) {
                let onPressMissionlink = () => {
                    void Linking.openURL(
                        `https://www.tempservice.ch/fr/tempdata/detail.php?cid=${missionCct.cctContractId}&vid=${missionCct.cctVersionId}&eid=${missionCct.cctEditionId}`,
                    );
                };
                if (missionCct && missionCct.intercomMainArticleId) {
                    onPressMissionlink = () => {
                        void presentArticleId(missionCct.intercomMainArticleId as string);
                    };
                }
                return (
                    <LinkSmallText onPress={onPressMissionlink}>
                        {i18n.t("screens.user.create_mission_cct.cct_details_link")}
                    </LinkSmallText>
                );
            }
        }
        return null;
    }, [missionCct, i18n, presentArticleId, liftBasedNoCct]);

    return (
        <VStack alignment="stretch">
            <BodyMediumText color={colors.body}>
                {!isPrivateClient
                    ? i18n.t("screens.user.create_mission_cct.description_top")
                    : i18n.t("screens.user.create_mission_cct.private_description_top")}
            </BodyMediumText>
            {!isPrivateClient && (
                <>
                    <Gap gap={1} />
                    <SelectField
                        testID="cct-select"
                        style={styles.items}
                        label={i18n.t("screens.user.create_mission_cct.title")}
                        value={missionCct?.active ? missionCct.cctContractId : undefined}
                        data={cct_choices}
                        isDynamicHeight
                        halfScreen
                        onSelect={(selectedItem) => {
                            const selectedCct = allCCT?.find(
                                (c) => c.cctContractId === selectedItem.value,
                            );
                            setCCTId(selectedCct);
                        }}
                        bottomComponent={
                            <VStack h="2/3">
                                <Gap gap="2/3" />
                                <MarkdownView
                                    markdown={i18n.t(
                                        "screens.user.create_mission_cct.no_cct_select",
                                    )}
                                    onLinkPress={() => {
                                        void presentMessageComposer();
                                        return false;
                                    }}
                                />
                            </VStack>
                        }
                    />
                    <Gap gap={1 / 2} />
                    {missionCct && !missionCct.active && (
                        <>
                            <Callout
                                mode="warning"
                                style={{ paddingTop: gridSize / 2, paddingBottom: gridSize / 2 }}
                                markdown={i18n.t(
                                    "screens.user.create_mission_cct.unsupported_cct",
                                    {
                                        cctName: missionCct.name,
                                    },
                                )}
                                onLinkPress={() => {
                                    void presentMessageComposer();
                                    return false;
                                }}
                            ></Callout>
                            <Gap gap={1 / 2} />
                        </>
                    )}
                    {missionCct && missionCct.cctEditionId === defaultCCT?.cctEditionId && (
                        <>
                            <BodySmallText color={colors.greyAccessible}>
                                {i18n.t("screens.user.create_mission_cct.default_cct_description")}
                            </BodySmallText>
                            <Gap gap={1 / 3} />
                        </>
                    )}
                    {cctLink}
                </>
            )}
            {missionCct && missionCct.active && (
                <>
                    <Gap gap={1} />
                    <MissionCctDetails
                        mission={mission}
                        setCctDetails={setCctDetails}
                        setScreenIsValid={setScreenIsValid}
                    />
                </>
            )}
        </VStack>
    );
};

const styles = StyleSheet.create({
    items: {
        textAlign: "auto",
        alignSelf: "stretch",
    },
});

export default MissionCCT;
